import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Divider,
	FormControl,
	Grid,
	IconButton,
	Link,
	ListItemIcon,
	MenuItem,
	Select,
	Snackbar,
	Typography,
	Tooltip,
	useTheme,
	useMediaQuery,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Fab from '@mui/material/Fab';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import GroupIcon from '@mui/icons-material/Group';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
	ICodeName,
	ISaleOrderDetail,
	IServices,
	PassengerTypeEnum,
	SegmentEnum,
	serviceTypes,
	TypeServiceEnum,
} from 'features/salesOrder/types';
import { Layout } from 'features/common/components/Layout';
import { ListMenu } from './components/ListMenu';
import { useServiceTypeMenu } from './components/AddServiceTypeMenu';
import { useLocation, useParams } from 'react-router-dom';
import { getCurrencies, patchEnableService } from 'features/salesOrder/services';
import { General } from './components/General';
import { Paxes } from './components/Paxes';
import { ProviderPayablesTab } from './components/ProviderPayables';
import { ClientPaymentsSection } from 'features/salesOrder/components/ClientPaymentsSection';
import { SaleOrderInvoices } from 'features/invoices/components/SaleOrderInvoices';
import { SaleOrderInvoicingProvider } from 'features/invoices/components/SaleOrderInvoices/InvoicingContext';
import InvoicingSummaryBox from 'features/invoices/components/SaleOrderInvoices/InvoicingSummaryBox';
import CreditLimitBanner from 'features/invoices/components/SaleOrderInvoices/CreditLimitBanner';
import { nanoidGenerator } from 'features/common/helpers';
// import { Customer } from './components/Customer';
import { ServiceForm } from './components/ServiceForm';
import { IValidatingCarrier } from 'features/transactions/types';
import { getValidatingCarriers } from 'features/transactions/services';
import { ShowAlertState } from 'features/common/types';
import CopyableId from './components/CopyableId';
import routes from 'constants/routes';
import DealOrCaseOpenDetail from '../../../features/common/components/DealOrCaseOpenDetail';
import { createNewPassengerGroup } from '../../../features/salesOrder/components/SalesOrderServiceForm/helpers';
import { focusServiceFormInitialField, generateSalesOrderUrl, generatePaymentsUrl } from './helpers';
import {
	getSaleOrderLoadErrorMessage,
	loadSaleOrderWithAgencyContext,
	parseAgencyIdFromSearch,
} from './helpers/saleOrderAgencyContext';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { ConfirmationModal } from 'features/common/components/ConfirmationModal/ConfirmationModal';
import { DeactivateServiceModal } from './components/DeactivateServiceModal';
import type { SaleOrderServiceRecord } from './components/DeactivateServiceModal/helpers';
import { ProviderPayablesProvider } from './context/ProviderPayablesContext';
import { CorporateCardPaymentsProvider } from 'features/corporateCards/context/CorporateCardPaymentsContext';

const defaultAlertState: ShowAlertState = { show: false, severity: 'success', message: '' };

export const SalesOrderScreen = (): JSX.Element => {
	const { id: saleOrderId } = useParams<{ id: string }>();
	const dispatch = useDispatch();
	const auth = useSelector((state: RootState) => state.auth);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const [loading, setLoading] = useState<boolean>(false);
	const [salesOrderData, setSalesOrderData] = useState<ISaleOrderDetail>();
	const [selectedItemIndex, setSelectedItemIndex] = useState<number | string>(0);
	const [validatingCarriers, setValidatingCarriers] = useState<IValidatingCarrier[]>([]);
	const [currencyesList, setCurrencyesList] = useState<ICodeName[]>([]);
	const [serviceState, setServiceState] = useState<IServices[] | undefined>();
	const [alertMsg, setAlertMsg] = useState<ShowAlertState>(defaultAlertState);
	const [copiedDealOrCaseId, setCopiedDealOrCaseId] = useState<boolean>(false);
	const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
	const [activateConfirmOpen, setActivateConfirmOpen] = useState(false);
	const [activatingService, setActivatingService] = useState(false);
	const [providerPayablesRefreshKey, setProviderPayablesRefreshKey] = useState(0);
	const selectedItemRef = useRef<number | string>(0);
	// Ref para la función de navegación del componente Paxes
	const paxesNavigationHandler = useRef<((callback: () => void) => void) | null>(null);
	const addServiceFabRef = useRef<HTMLButtonElement>(null);
	const addServiceFocusDoneRef = useRef(false);
	const saleOrderAgencyIdRef = useRef<number | undefined>(undefined);
	/**
	 * La misma agencia que el ref, pero en estado: el provider de facturación la
	 * necesita para elegir la API key, y un ref no vuelve a renderizar.
	 */
	const [agencyIdForRequests, setAgencyIdForRequests] = useState<number | undefined>(undefined);

	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const serviceIdFromURL = queryParams.get('serviceId');
	const passengerIdFromURL = queryParams.get('passengerId');
	const sectionFromURL = queryParams.get('section');
	const dealOrCaseId = salesOrderData?.dealId;

	const handleCopyDealOrCaseId = useCallback(() => {
		if (!dealOrCaseId) {
			return;
		}

		navigator.clipboard.writeText(dealOrCaseId.toString());
		setCopiedDealOrCaseId(true);
		setTimeout(() => {
			setCopiedDealOrCaseId(false);
		}, 2000);
	}, [dealOrCaseId]);

	const loadValidatinCarries = useCallback(async () => {
		try {
			setLoading(true);
			setValidatingCarriers((await getValidatingCarriers()).data);
		} catch (error) {
			// @ts-ignore
			alert(error.response.data.message);
			console.log(error);
		} finally {
			setLoading(false);
		}
	}, []);

	const loadCurrencies = useCallback(async () => {
		try {
			setLoading(true);
			const currencyes = (await getCurrencies()).data;
			setCurrencyesList(currencyes);
		} catch (error) {
			// @ts-ignore
			alert(error.response.data.message);
		} finally {
			setLoading(false);
		}
	}, []);

	const handleListItemClick = (_event: React.MouseEvent<HTMLDivElement, MouseEvent>, index: number | string) => {
		// Función que ejecuta la navegación real
		const executeNavigation = () => {
			setSelectedItemIndex(index);
			selectedItemRef.current = index;
			const agencyId = saleOrderAgencyIdRef.current;
			// Actualizar la URL sin causar recarga usando la API nativa del navegador
			if (typeof index === 'string') {
				const newUrl = generateSalesOrderUrl(saleOrderId, index, agencyId);
				window.history.replaceState(null, '', newUrl);
			} else if (index === 2) {
				const newUrl = generatePaymentsUrl(saleOrderId);
				window.history.replaceState(null, '', newUrl);
			} else {
				// General (0) o Pasajeros (1)
				const newUrl = generateSalesOrderUrl(saleOrderId, undefined, agencyId);
				window.history.replaceState(null, '', newUrl);
			}
		};

		// Si estamos saliendo de la sección de pasajeros (1) y tenemos el handler de navegación
		if (selectedItemIndex === 1 && index !== 1 && paxesNavigationHandler.current) {
			// Interceptar la navegación para verificar cambios no guardados
			paxesNavigationHandler.current(executeNavigation);
		} else {
			// Navegación normal para otras secciones
			executeNavigation();
		}
	};

	const fetchOrder = useCallback(
		async (preserveSelection = false) => {
			try {
				setLoading(true);
				const currentSelection = selectedItemRef.current;
				const preferredAgencyId = parseAgencyIdFromSearch(location.search) ?? saleOrderAgencyIdRef.current;
				const userAgencyIds = auth.user?.agencies.map((agency) => agency.agencyId) ?? [];

				const { data, resolvedAgencyId, switched, countryName } = await loadSaleOrderWithAgencyContext({
					saleOrderId,
					preferredAgencyId,
					userAgencyIds,
					currentCountry: auth.country,
					dispatch,
				});

				saleOrderAgencyIdRef.current = resolvedAgencyId;
				setAgencyIdForRequests(resolvedAgencyId);

				if (switched) {
					setAlertMsg({
						show: true,
						severity: 'info',
						message: `Cambiaste automáticamente a ${countryName} para ver esta orden de venta.`,
					});
				}

				const services = data.services ?? [];
				setSalesOrderData({ ...data, services });

				// Leer serviceId de la URL actual, no del valor inicial
				const currentUrl = new URL(window.location.href);
				const currentServiceId = currentUrl.searchParams.get('serviceId');

				if (!parseAgencyIdFromSearch(location.search)) {
					const syncUrl = generateSalesOrderUrl(saleOrderId, currentServiceId ?? undefined, resolvedAgencyId);
					window.history.replaceState(null, '', syncUrl);
				}

				if (currentServiceId) {
					const existService = services.some((service) => service.keyId === currentServiceId);
					if (existService) {
						setSelectedItemIndex(currentServiceId);
						selectedItemRef.current = currentServiceId;
						return; // Salir temprano, ya encontramos el servicio de la URL
					}
				}

				if (preserveSelection && typeof currentSelection === 'string') {
					// Verificar si el servicio actualmente seleccionado todavía existe
					const currentServiceExists = services.some((service) => service.keyId === currentSelection);
					if (currentServiceExists) {
						// Mantener la selección actual
						setSelectedItemIndex(currentSelection);
						selectedItemRef.current = currentSelection;
					}
				}
			} catch (error) {
				setAlertMsg({
					show: true,
					severity: 'error',
					message: getSaleOrderLoadErrorMessage(error),
				});
			} finally {
				setLoading(false);
			}
		},
		[saleOrderId, location.search, auth.user, auth.country, dispatch],
	);

	// Sincronizar ref con el estado
	useEffect(() => {
		selectedItemRef.current = selectedItemIndex;
	}, [selectedItemIndex]);

	useEffect(() => {
		if (passengerIdFromURL) {
			setSelectedItemIndex(1);
			selectedItemRef.current = 1;
		}
	}, [passengerIdFromURL]);

	useEffect(() => {
		if (sectionFromURL === 'payments') {
			setSelectedItemIndex(2);
			selectedItemRef.current = 2;
		} else if (sectionFromURL === 'cobros-facturas') {
			setSelectedItemIndex(3);
			selectedItemRef.current = 3;
		}
	}, [sectionFromURL]);

	// Escuchar el evento personalizado para navegar al servicio desde la tabla General
	useEffect(() => {
		const handleNavigateToService = (event: CustomEvent) => {
			const { serviceId } = event.detail;
			setSelectedItemIndex(serviceId);
			selectedItemRef.current = serviceId;
		};

		window.addEventListener('navigate-to-service', handleNavigateToService as EventListener);

		return () => {
			window.removeEventListener('navigate-to-service', handleNavigateToService as EventListener);
		};
	}, []);

	const handleAddNewService = useCallback(
		(serviceType: TypeServiceEnum) => {
			const newServiceKeyId = nanoidGenerator();
			const isFlightBasedService =
				serviceType === TypeServiceEnum.Flight || serviceType === TypeServiceEnum.FlightAncillary;
			const newService = {
				keyId: newServiceKeyId,
				reference: '',
				tariffProviderId: '',
				unit: [],
				cost: '',
				saleAmount: '',
				currency: '',
				markupPercentage: '',
				passengerIds: [],
				serviceProviderId: '',
				serviceType,
				...(isFlightBasedService && {
					passengersGroupByPassengerType: [createNewPassengerGroup(PassengerTypeEnum.A)],
				}),
				automaticProviderPaymentDateCalculation: true,
			};
			// @ts-ignore
			setServiceState((prevState) => [...prevState, newService]);

			// Navegar automáticamente al nuevo servicio creado
			setSelectedItemIndex(newServiceKeyId);
			selectedItemRef.current = newServiceKeyId;

			// Actualizar URL para reflejar el nuevo servicio
			const newUrl = generateSalesOrderUrl(saleOrderId, newServiceKeyId, saleOrderAgencyIdRef.current);
			window.history.replaceState(null, '', newUrl);
		},
		[saleOrderId, setServiceState],
	);

	const {
		handleOpen: handleOpenAddServiceMenu,
		menu: addServiceTypeMenu,
		open: addServiceMenuOpen,
		menuId: addServiceMenuId,
	} = useServiceTypeMenu({
		onSelect: handleAddNewService,
		triggerId: 'add-service-fab-button',
		menuAnchorOrigin: { vertical: 'top', horizontal: 'right' },
	});

	const handleChangeServiceType = useCallback(
		(newValue: TypeServiceEnum, index: number) => {
			setServiceState((prevState) => {
				const newState = prevState ? [...prevState] : [];
				if (newState[index]) {
					newState[index].serviceType = newValue;
				}
				return newState;
			});
		},
		[setServiceState],
	);

	useEffect(() => {
		if (salesOrderData?.services) {
			setServiceState(salesOrderData.services);
		}
	}, [salesOrderData?.services]);

	// Función específica para actualizar después de guardar servicios
	const handleServiceUpdate = useCallback(() => {
		fetchOrder(true); // preserveSelection = true
		setProviderPayablesRefreshKey((key) => key + 1);
	}, [fetchOrder]);

	useEffect(() => {
		saleOrderAgencyIdRef.current = parseAgencyIdFromSearch(location.search);
		loadValidatinCarries();
		loadCurrencies();
		fetchOrder();
	}, [saleOrderId, fetchOrder, setLoading, loadValidatinCarries, loadCurrencies]);

	const serviceIndex = useMemo(
		() => serviceState?.findIndex((service) => service.keyId === selectedItemIndex),
		[serviceState, selectedItemIndex],
	);

	const isExistingService = useMemo(() => {
		if (serviceState && serviceIndex !== undefined && serviceState[serviceIndex]) {
			const result = Boolean(
				salesOrderData?.services?.find((service) => service.keyId === serviceState[serviceIndex].keyId),
			);
			return result;
		}
		return false;
	}, [serviceIndex, serviceState, salesOrderData]);

	const isEnabled = useMemo(() => {
		if (serviceIndex === undefined || !salesOrderData?.services[serviceIndex]) {
			return true;
		}
		return salesOrderData.services[serviceIndex].enable !== false;
	}, [serviceIndex, salesOrderData]);

	const selectedServiceForModal = useMemo((): SaleOrderServiceRecord | null => {
		if (serviceIndex === undefined || !salesOrderData?.services[serviceIndex]) {
			return null;
		}
		return salesOrderData.services[serviceIndex] as unknown as SaleOrderServiceRecord;
	}, [serviceIndex, salesOrderData]);

	const isCreatingNewService = useMemo(() => {
		return typeof selectedItemIndex === 'string' && !isExistingService;
	}, [selectedItemIndex, isExistingService]);

	const isSaleOrderHomeView = useMemo(() => {
		if (!salesOrderData) {
			return false;
		}
		if (serviceIdFromURL) {
			return false;
		}
		return typeof selectedItemIndex === 'number' && selectedItemIndex === 0;
	}, [salesOrderData, serviceIdFromURL, selectedItemIndex]);

	const shouldFocusAddServiceButton = isSaleOrderHomeView;

	const tryFocusAddServiceButton = useCallback(() => {
		const target = isMobile ? addServiceFabRef.current : document.getElementById('add-service-button');
		if (!(target instanceof HTMLElement)) {
			return false;
		}
		target.focus();
		return document.activeElement === target;
	}, [isMobile]);

	useEffect(() => {
		addServiceFocusDoneRef.current = false;
	}, [saleOrderId]);

	useEffect(() => {
		if (typeof selectedItemIndex === 'number' && selectedItemIndex === 0 && !serviceIdFromURL) {
			addServiceFocusDoneRef.current = false;
		}
	}, [selectedItemIndex, serviceIdFromURL]);

	const canFocusAddServiceButton = shouldFocusAddServiceButton && !loading;

	useLayoutEffect(() => {
		if (!canFocusAddServiceButton || addServiceFocusDoneRef.current) {
			return;
		}
		if (tryFocusAddServiceButton()) {
			addServiceFocusDoneRef.current = true;
		}
	}, [canFocusAddServiceButton, tryFocusAddServiceButton]);

	useEffect(() => {
		if (!canFocusAddServiceButton || addServiceFocusDoneRef.current) {
			return;
		}

		const attemptFocus = () => {
			if (addServiceFocusDoneRef.current) {
				return true;
			}
			if (tryFocusAddServiceButton()) {
				addServiceFocusDoneRef.current = true;
				return true;
			}
			return false;
		};

		if (attemptFocus()) {
			return;
		}

		const delays = [50, 150, 350, 700];
		const timeoutIds = delays.map((delay) =>
			window.setTimeout(() => {
				attemptFocus();
			}, delay),
		);

		return () => {
			timeoutIds.forEach((id) => window.clearTimeout(id));
		};
	}, [canFocusAddServiceButton, tryFocusAddServiceButton]);

	useEffect(() => {
		if (!isCreatingNewService) {
			return;
		}

		const attemptFocus = () => focusServiceFormInitialField();

		if (attemptFocus()) {
			return;
		}

		const delays = [50, 150, 300, 500];
		const timeoutIds = delays.map((delay) => window.setTimeout(attemptFocus, delay));

		return () => {
			timeoutIds.forEach((id) => window.clearTimeout(id));
		};
	}, [isCreatingNewService, selectedItemIndex]);

	const isDeactivateServiceAction = isExistingService && isEnabled;

	const serviceActionButtonColor = useMemo((): 'error' | 'success' | 'inherit' => {
		if (!isExistingService) {
			return 'error';
		}
		// inherit + sx: el theme define warning en violeta; desactivar usa naranja solo acá
		return isEnabled ? 'inherit' : 'success';
	}, [isExistingService, isEnabled]);

	const deactivateServiceButtonSx = {
		backgroundColor: '#ed6c02',
		color: '#fff',
		'&:hover': {
			backgroundColor: '#e65100',
		},
	};

	const handleActivateServiceConfirm = useCallback(async () => {
		if (serviceIndex === undefined || !salesOrderData?.services[serviceIndex]?.keyId) {
			return;
		}

		try {
			setActivatingService(true);
			await patchEnableService({
				saleOrderId,
				serviceId: salesOrderData.services[serviceIndex].keyId,
				enable: true,
			});
			setActivateConfirmOpen(false);
			handleServiceUpdate();
			setAlertMsg({
				show: true,
				severity: 'success',
				message: 'Servicio activado con éxito',
			});
		} catch (e) {
			// @ts-ignore
			alert(e.response?.data?.message ?? 'Ocurrió un error al activar el servicio');
		} finally {
			setActivatingService(false);
		}
	}, [serviceIndex, salesOrderData, saleOrderId, handleServiceUpdate]);

	const handleRemoveService = useCallback(() => {
		if (serviceIndex === undefined) {
			return;
		}

		if (isExistingService) {
			if (isEnabled) {
				setDeactivateModalOpen(true);
				return;
			}

			setActivateConfirmOpen(true);
			return;
		}

		setServiceState((prevState) => {
			const newState = prevState && [...prevState];
			newState?.splice(serviceIndex, 1);
			return newState;
		});

		setSelectedItemIndex(0);
		selectedItemRef.current = 0;
		const newUrl = generateSalesOrderUrl(saleOrderId, '0', saleOrderAgencyIdRef.current);
		window.history.replaceState(null, '', newUrl);
	}, [isExistingService, isEnabled, serviceIndex, saleOrderId]);

	const returnSelectedServiceType = (): TypeServiceEnum | null => {
		if (serviceState && serviceIndex !== undefined && serviceState[serviceIndex]) {
			return serviceState[serviceIndex].serviceType as TypeServiceEnum;
		}
		return null;
	};

	const invoiceablePassengers = salesOrderData?.passengers.filter((passenger) => passenger.invoiceable) || [];
	const filteredPassengers = invoiceablePassengers.map((passenger) => ({
		keyId: passenger.keyId,
		name: passenger.name,
		lastName: passenger.lastName,
		isPhysicalPerson: passenger.isPhysicalPerson,
	}));

	const handleOpenClientPage = (passengerKeyId: string): void => {
		const agencyId = saleOrderAgencyIdRef.current;
		const url = `${routes.passengers.detail.replace(':id', passengerKeyId)}?from=customers${
			agencyId ? `&agencyId=${agencyId}` : ''
		}`;
		window.open(url, '_blank', 'noopener,noreferrer');
	};

	return (
		<>
			{loading && (
				<Box
					sx={{
						background: 'rgba(0,0,0,0.5)',
						position: 'fixed',
						top: 0,
						left: 0,
						width: '100vw',
						height: '100vh',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						zIndex: 999999999,
					}}
				>
					<CircularProgress color="primary" />
				</Box>
			)}
			<Layout>
				<ProviderPayablesProvider saleOrderId={saleOrderId ?? ''} refreshKey={providerPayablesRefreshKey}>
					<CorporateCardPaymentsProvider saleOrderId={saleOrderId ?? ''}>
						<SaleOrderInvoicingProvider
							saleOrderId={saleOrderId ?? ''}
							agencyId={agencyIdForRequests}
							invoiceablePassengerIds={invoiceablePassengers.map((p) => p.keyId)}
						>
							<Grid padding={isMobile ? 1 : 2} container spacing={isMobile ? 1 : 2}>
								{/* Ocultar ListMenu en mobile */}
								{!isMobile && (
									<Grid item xs={12} sm={3}>
										<ListMenu
											onAddNewService={handleAddNewService}
											services={serviceState || []}
											passengers={salesOrderData?.passengers || []}
											onClick={handleListItemClick}
											selectedItemIndex={selectedItemIndex}
										/>
									</Grid>
								)}

								<Grid item xs={12} sm={isMobile ? 12 : 9}>
									{isMobile ? (
										// Layout para mobile: cards apiladas verticalmente
										<Box
											sx={{
												display: 'flex',
												flexDirection: 'column',
												gap: 2,
												mb: 2,
											}}
										>
											<Box
												sx={{
													p: 2,
													border: '1px solid #e0e0e0',
													borderRadius: 2,
													backgroundColor: '#fafafa',
												}}
											>
												{filteredPassengers.length > 0 ? (
													filteredPassengers.map((passenger, index) => (
														<Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
															<strong>Factura a:</strong>{' '}
															<Box
																component="span"
																sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
																onClick={() => handleOpenClientPage(passenger.keyId)}
															>
																{passenger.name} {passenger.lastName}
															</Box>{' '}
															| {passenger.isPhysicalPerson ? 'Persona' : 'Empresa'}
														</Typography>
													))
												) : (
													<Typography variant="body2" color="error">
														No hay pasajero para facturar
													</Typography>
												)}
											</Box>

											<Box
												sx={{
													p: 2,
													border: '1px solid #e0e0e0',
													borderRadius: 2,
													backgroundColor: '#fafafa',
												}}
											>
												<Typography variant="body2" sx={{ mb: 1 }}>
													<Box component="span" display="inline-flex" alignItems="center" gap={1}>
														<strong>{salesOrderData?.isBitrixDeal ? 'Deal' : 'Caso'}:</strong>{' '}
														{salesOrderData && (
															<>
																<DealOrCaseOpenDetail
																	dealId={salesOrderData.dealId}
																	isBitrixDeal={salesOrderData.isBitrixDeal}
																/>
																<Tooltip
																	title={
																		copiedDealOrCaseId
																			? 'Copiado satisfactoriamente'
																			: `Copiar ${salesOrderData?.isBitrixDeal ? 'deal' : 'caso'}`
																	}
																>
																	<span>
																		<IconButton onClick={handleCopyDealOrCaseId} size="small" disabled={!dealOrCaseId}>
																			{copiedDealOrCaseId ? (
																				<CheckIcon fontSize="small" sx={{ color: 'green' }} />
																			) : (
																				<ContentCopyIcon fontSize="small" />
																			)}
																		</IconButton>
																	</span>
																</Tooltip>
															</>
														)}
													</Box>
												</Typography>
											</Box>

											{/* Box de Cotización ocultado en mobile */}
											{/* <Box
										sx={{
											p: 2,
											border: '1px solid #e0e0e0',
											borderRadius: 2,
											backgroundColor: '#fafafa'
										}}
									>
										<Typography variant="body2">
											<strong>Cotizacion:</strong>{' '}
											<Link
												href={`https://power.mevuelo.com/quotations/${salesOrderData?.quotationId}/`}
												target="_blank"
												rel="noopener noreferrer"
											>
												{salesOrderData?.quotationId}
											</Link>
										</Typography>
									</Box> */}
										</Box>
									) : (
										// Layout para desktop: horizontal con divisores
										<Grid
											container
											spacing={2}
											sx={{
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
											}}
										>
											<Grid item xs={6}>
												{filteredPassengers.length > 0 ? (
													filteredPassengers.map((passenger, index) => (
														<div key={index}>
															<Typography variant="h6">
																Factura a:{' '}
																<Tooltip title="Abrir la página del cliente en otra pestaña">
																	<Box
																		component="span"
																		sx={{
																			color: 'primary.main',
																			cursor: 'pointer',
																			'&:hover': { textDecoration: 'underline' },
																		}}
																		onClick={() => handleOpenClientPage(passenger.keyId)}
																	>
																		{passenger.name} {passenger.lastName}
																		<OpenInNewIcon sx={{ fontSize: 16, ml: 0.5, verticalAlign: 'middle' }} />
																	</Box>
																</Tooltip>{' '}
																| <span>{passenger.isPhysicalPerson ? 'Persona' : 'Empresa'}</span>
															</Typography>
														</div>
													))
												) : (
													<Typography variant="h6" color="error">
														No hay pasajero para facturar
													</Typography>
												)}
											</Grid>
											<Divider orientation="vertical" flexItem sx={{ my: 2, mx: 1, mb: 1 }} />
											<Grid item xs={2}>
												<Typography variant="h6">
													<Box component="span" display="inline-flex" alignItems="center" gap={1}>
														{salesOrderData?.isBitrixDeal ? 'Deal' : 'Caso'}{' '}
														{salesOrderData && (
															<>
																<DealOrCaseOpenDetail
																	dealId={salesOrderData.dealId}
																	isBitrixDeal={salesOrderData.isBitrixDeal}
																/>
																<Tooltip
																	title={
																		copiedDealOrCaseId
																			? 'Copiado satisfactoriamente'
																			: `Copiar ${salesOrderData?.isBitrixDeal ? 'deal' : 'caso'}`
																	}
																>
																	<span>
																		<IconButton onClick={handleCopyDealOrCaseId} size="small" disabled={!dealOrCaseId}>
																			{copiedDealOrCaseId ? (
																				<CheckIcon fontSize="small" sx={{ color: 'green' }} />
																			) : (
																				<ContentCopyIcon fontSize="small" />
																			)}
																		</IconButton>
																	</span>
																</Tooltip>
															</>
														)}
													</Box>
												</Typography>
											</Grid>
											<Divider orientation="vertical" flexItem sx={{ my: 2, mx: 1, mb: 1 }} />
											<Grid item xs={3}>
												<Typography variant="h6">
													Cotizacion:{' '}
													<span>
														<Link
															href={`https://power.mevuelo.com/quotations/${salesOrderData?.quotationId}/`}
															target="_blank"
															rel="noopener noreferrer"
														>
															{salesOrderData?.quotationId}
														</Link>
													</span>
												</Typography>
											</Grid>
										</Grid>
									)}

									{/* Indicadores de facturación (lazy, comparte cache con la pestaña Facturas) */}
									{selectedItemIndex === 0 && salesOrderData && (
										<Box sx={{ px: isMobile ? 2 : 3 }}>
											<InvoicingSummaryBox
												services={salesOrderData.services}
												segmentType={salesOrderData.segmentType}
												travelReturnDate={salesOrderData.travelReturnDate}
												onGoToInvoices={() =>
													handleListItemClick({} as React.MouseEvent<HTMLDivElement, MouseEvent>, 3)
												}
											/>
											{/* Control de crédito: solo cuentas (no aplica a Leisure) */}
											{salesOrderData.segmentType !== SegmentEnum.LEISURE && <CreditLimitBanner />}
										</Box>
									)}

									{/* Navegación móvil */}
									{isMobile && (
										<Box sx={{ mb: 2 }}>
											<FormControl fullWidth size="small">
												<Select
													value={selectedItemIndex}
													onChange={(e) =>
														handleListItemClick(
															{} as React.MouseEvent<HTMLDivElement, MouseEvent>,
															e.target.value as number | string,
														)
													}
													displayEmpty
												>
													<MenuItem value={0}>
														<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
															<QueryStatsIcon fontSize="small" />
															General
														</Box>
													</MenuItem>
													<MenuItem value={1}>
														<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
															<GroupIcon fontSize="small" />
															Pasajeros
														</Box>
													</MenuItem>
													<MenuItem value={2}>
														<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>Pagos Proveedores</Box>
													</MenuItem>
													<MenuItem value={3}>
														<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>Recibos y Facturas</Box>
													</MenuItem>
													{serviceState?.map((service) => {
														const serviceType = serviceTypes.find((st) => st.id === service.serviceType);
														const isNewService = !salesOrderData?.services?.find((s) => s.keyId === service.keyId);

														return (
															<MenuItem key={service.keyId} value={service.keyId} disabled={isNewService}>
																<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
																	{isNewService ? (
																		<Box sx={{ display: 'flex', alignItems: 'center', fontSize: '1.25rem' }}>
																			<HelpOutlineIcon fontSize="small" />
																		</Box>
																	) : (
																		serviceType?.icon && (
																			<Box sx={{ display: 'flex', alignItems: 'center', fontSize: '1.25rem' }}>
																				{serviceType.icon}
																			</Box>
																		)
																	)}
																	<Box sx={{ flex: 1 }}>
																		<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
																			{isNewService ? 'Nuevo servicio' : serviceType?.name || 'Servicio'}
																		</Typography>
																		<Typography variant="caption" color="text.secondary">
																			{isNewService
																				? 'Selecciona el tipo de servicio'
																				: (service as any).reference || 'Sin referencia'}
																		</Typography>
																	</Box>
																</Box>
															</MenuItem>
														);
													})}
												</Select>
											</FormControl>
										</Box>
									)}

									<Divider orientation="horizontal" flexItem sx={{ my: 2, mx: 1, mb: 1 }} />

									{selectedItemIndex === 0 && salesOrderData && (
										<General
											onUpdate={handleServiceUpdate}
											onSetAlert={setAlertMsg}
											data={salesOrderData}
											loading={loading}
											setLoading={setLoading}
										/>
									)}
									{selectedItemIndex === 1 && salesOrderData && (
										<Paxes
											passengers={salesOrderData?.passengers || []}
											onUpdatePassengers={handleServiceUpdate}
											onSetAlert={setAlertMsg}
											onNavigationAttempt={(handler) => {
												paxesNavigationHandler.current = handler;
											}}
										/>
									)}
									{selectedItemIndex === 2 && <ProviderPayablesTab saleOrderId={saleOrderId} />}
									{selectedItemIndex === 3 && salesOrderData && (
										<>
											<ClientPaymentsSection
												saleOrderId={saleOrderId}
												saleAmount={salesOrderData.saleAmount}
												passengers={salesOrderData.passengers}
												canWrite={true}
											/>
											<Divider sx={{ my: 3 }} />
											<SaleOrderInvoices
												saleOrderId={saleOrderId}
												passengers={salesOrderData.passengers}
												services={salesOrderData.services}
												customer={salesOrderData.customer}
												canWrite={true}
												segmentType={salesOrderData.segmentType}
												clientReference={salesOrderData.clientReference}
												travelDepartureDate={salesOrderData.travelDepartureDate}
											/>
										</>
									)}
									{/* {selectedItemIndex === 4 && salesOrderData && (
								<Customer
									segmentType={salesOrderData.segmentType}
									customer={salesOrderData.customer}
									id={saleOrderId}
									customerToShow={salesOrderData.passengers.find((pax) => pax.mainPassenger === true)}
								/>
							)} */}

									{typeof selectedItemIndex === 'string' && serviceState && serviceIndex !== undefined && (
										<>
											{/* Select de tipo de servicio en mobile - estilo como los boxes de arriba */}
											{isMobile && !isExistingService && (
												<Box
													sx={{
														p: 2,
														border: '1px solid #e0e0e0',
														borderRadius: 2,
														backgroundColor: '#fafafa',
														mb: 2,
														display: 'flex',
														alignItems: 'center',
														gap: 2,
													}}
												>
													<Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: '50px' }}>
														Tipo:
													</Typography>
													<FormControl variant="outlined" size="small" sx={{ flex: 1 }}>
														<Select
															required
															size="small"
															value={returnSelectedServiceType()}
															onChange={(e) => handleChangeServiceType(e.target.value as TypeServiceEnum, serviceIndex)}
														>
															{serviceTypes.map((serviceType) => (
																<MenuItem key={serviceType.id} value={serviceType.id}>
																	<Box display="flex" gap={1} alignItems="center">
																		{serviceType.icon}
																		<Typography color={'black'}>{serviceType.name}</Typography>
																	</Box>
																</MenuItem>
															))}
														</Select>
													</FormControl>
												</Box>
											)}

											<Box
												width="100%"
												pl={isMobile ? 1 : 2}
												pr={isMobile ? 1 : 2}
												display="flex"
												justifyContent="space-between"
												flexDirection={isMobile ? 'column' : 'row'}
												gap={isMobile ? 2 : 0}
											>
												{/* Mostrar select de tipo de servicio solo en desktop */}
												{!isMobile && (
													<FormControl variant="outlined" size="small" sx={{ minWidth: 'auto' }}>
														<Select
															required
															size="small"
															value={returnSelectedServiceType()}
															onChange={(e) => handleChangeServiceType(e.target.value as TypeServiceEnum, serviceIndex)}
														>
															{serviceTypes.map((serviceType) => (
																<MenuItem key={serviceType.id} value={serviceType.id}>
																	<ListItemIcon sx={{ mt: 1 }}>
																		<Box display="flex" gap={1}>
																			{serviceType.icon}
																			<Typography color={'black'}>{serviceType.name}</Typography>
																		</Box>
																	</ListItemIcon>
																</MenuItem>
															))}
														</Select>
													</FormControl>
												)}
												<CopyableId copiableIdValue={selectedItemIndex} />

												{isMobile ? (
													<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
														<Button
															onClick={handleRemoveService}
															size="medium"
															variant="contained"
															color={serviceActionButtonColor}
															sx={{
																...(isDeactivateServiceAction ? deactivateServiceButtonSx : {}),
																minWidth: '200px',
																maxWidth: '250px',
															}}
														>
															<DeleteIcon sx={{ mr: 1 }} />
															{isExistingService ? (isEnabled ? 'Desactivar' : 'Activar') : 'Eliminar'} servicio
														</Button>
													</Box>
												) : (
													<Box pr={4}>
														<Button
															onClick={handleRemoveService}
															size="large"
															variant="contained"
															color={serviceActionButtonColor}
															sx={isDeactivateServiceAction ? deactivateServiceButtonSx : undefined}
														>
															<DeleteIcon />
															{isExistingService ? (isEnabled ? 'Desactivar' : 'Activar') : 'Eliminar'} servicio
														</Button>
													</Box>
												)}
											</Box>

											<ServiceForm
												onSetAlert={setAlertMsg}
												isExistingService={isExistingService}
												onUpdateService={handleServiceUpdate}
												currencyesList={currencyesList}
												serviceIndex={serviceState?.findIndex((service) => service.keyId === selectedItemIndex)}
												validatingCarriers={validatingCarriers}
												passengers={salesOrderData?.passengers || []}
												service={serviceState?.find((service) => service.keyId === selectedItemIndex)}
												isEnabled={isEnabled}
												saleOrderId={saleOrderId}
											/>
										</>
									)}
								</Grid>
							</Grid>
							<Snackbar open={alertMsg.show} onClose={() => setAlertMsg(defaultAlertState)}>
								<Alert variant="filled" severity={alertMsg.severity}>
									{alertMsg.message}
								</Alert>
							</Snackbar>

							<DeactivateServiceModal
								open={deactivateModalOpen}
								onClose={() => setDeactivateModalOpen(false)}
								saleOrderId={saleOrderId}
								service={selectedServiceForModal}
								passengers={salesOrderData?.passengers ?? []}
								onSuccess={() => {
									handleServiceUpdate();
									setAlertMsg({
										show: true,
										severity: 'success',
										message: 'Servicio desactivado con éxito',
									});
								}}
							/>

							<ConfirmationModal
								open={activateConfirmOpen}
								title="Activar servicio"
								message="¿Confirmás que querés reactivar este servicio? Volverá a considerarse como servicio activo en la orden, lo cual implica prestarle el servicio al cliente. En tal caso, verificá el costo y el valor de venta una vez activo."
								handleAccept={() => void handleActivateServiceConfirm()}
								handleClose={() => setActivateConfirmOpen(false)}
								loading={activatingService}
							/>

							{/* Botón flotante para agregar servicios en mobile */}
							{isMobile && !isCreatingNewService && (
								<>
									<Fab
										ref={addServiceFabRef}
										id="add-service-fab-button"
										color="primary"
										aria-label="Agregar servicio"
										aria-controls={addServiceMenuOpen ? addServiceMenuId : undefined}
										aria-haspopup="true"
										aria-expanded={addServiceMenuOpen ? 'true' : undefined}
										onClick={handleOpenAddServiceMenu}
										sx={{
											position: 'fixed',
											bottom: 16,
											right: 16,
											zIndex: 1000,
										}}
									>
										<AddIcon />
									</Fab>
									{addServiceTypeMenu}
								</>
							)}
						</SaleOrderInvoicingProvider>
					</CorporateCardPaymentsProvider>
				</ProviderPayablesProvider>
			</Layout>
		</>
	);
};
